import { Card, Col, Row, Typography } from "antd";
import { EnvironmentOutlined, PhoneOutlined, UserOutlined, TeamOutlined } from "@ant-design/icons";
import { Event, EventResponsibility, Location } from "interfaces/events";
import { useHistory } from "react-router-dom";
import useViewport from "components/UseViewport/UseViewport";
import CompanyLogo from "components/CompanyLogo/CompanyLogo";
import { useEffect, useState } from "react";
import { CompanyContact } from "interfaces/companies";
import axios from "axios";
import { Member } from "interfaces/users";
import { ResponsibilityType } from "../CreateOrEditEvent/CreateOrEditEvent";

const { Title, Text } = Typography;

interface Props {
  event: Event;
}

const AdminSingleEventHeader: React.FC<Props> = ({ event }) => {
  const { width } = useViewport();
  const breakpoint = 900;

  const [companyContact, setCompanyContact] = useState<CompanyContact>();
  const [location, setLocation] = useState<Location>();
  const [members, setMembers] = useState<Member[]>([]);
  const [responsibilities, setResponsibilities] = useState<EventResponsibility[]>();
  const [ha, setHa] = useState<string>("");
  const [mp, setMp] = useState<string>("");

  const history = useHistory();

  useEffect(() => {
    if (!event || !event.location) return;
    axios.get<Location>(`/events/locations/${event.location}/`).then(response => {
      setLocation(response.data);
    });
  }, [event]);

  useEffect(() => {
    if (!event || !event?.company_contact_id) return;
    axios
      .get<CompanyContact>(`/companies/${event.company.id}/contact-persons/${event.company_contact_id}/`)
      .then(response => {
        setCompanyContact(response.data);
      });
  }, [event]);

  useEffect(() => {
    axios.get<Member[]>(`/internal/members/`).then(response => {
      setMembers(response.data);
    });
  }, []);

  useEffect(() => {
    if (!event) return;
    axios.get<EventResponsibility[]>(`/events/${event.id}/event-responsibility/`).then(response => {
      setResponsibilities(response.data);
    });
  }, [event]);

  useEffect(() => {
    if (members.length === 0 || !responsibilities) return;
    const MPs = responsibilities
      .filter(r => r.responsibility_type === ResponsibilityType.MEETING_RESPONSIBLE)
      .map(r => {
        const member = members.find(m => m.id === r.bl_member);
        return member ? member.full_name : "Ukjent medlem";
      })
      .join(", ");
    setMp(MPs.length > 0 ? MPs : "Ikke bestemt");
    const HAs = responsibilities
      .filter(r => r.responsibility_type === ResponsibilityType.MAIN_RESPONSIBLE)
      .map(r => {
        const member = members.find(m => m.id === r.bl_member);
        return member ? member.full_name : "Ukjent medlem";
      })
      .join(", ");
    setHa(HAs.length > 0 ? HAs : "Ikke bestemt");
  }, [members, responsibilities]);

  if (!event) return null;

  const getTime = () => {
    const date = new Date(event.start);
    return `${date?.getHours()}:${date.getMinutes() < 10 ? "0" : ""}${date.getMinutes()}`;
  };

  const getDate = () => {
    const date = new Date(event.start);
    return `${date.getDate()}. ${date.toLocaleString("default", { month: "long" })} ${date.getFullYear()}`;
  };

  return (
    <>
      <Card style={{ width: "100%", position: "relative", alignItems: "end" }}>
        <Row gutter={10}>
          <Col span={width < breakpoint ? 24 : 4}>
            <CompanyLogo
              size="95%"
              companyId={event.company.id}
              src={event.company.logo}
              onClick={() => history.push(`/admin/companies/${event.company.id}/`)}
            />
          </Col>
          <Col span={width < breakpoint ? 24 : 12} style={width >= breakpoint ? { paddingTop: "10px" } : {}}>
            <Row>
              <Title style={{ margin: 0 }}>{event.name}</Title>
            </Row>
            <Row gutter={10} style={{ padding: "5px 0" }}>
              <Col>
                <Text>
                  <EnvironmentOutlined />
                  {location?.name ?? "Ikke bestemt"}
                </Text>
              </Col>
              <Col>
                <Text>
                  <TeamOutlined />
                  {event.registrations}/{event.seats}
                </Text>
              </Col>
              {event.company_contact_id ? (
                <>
                  <Col>
                    <Text>
                      <UserOutlined />
                      {companyContact?.first_name} {companyContact?.last_name}
                    </Text>
                  </Col>
                  <Col>
                    <Text>
                      <PhoneOutlined />
                      {companyContact?.telephone}
                    </Text>
                  </Col>
                </>
              ) : (
                <Col>
                  <Text>
                    <UserOutlined />
                    Kontakt hos bedriften ikke bestemt
                  </Text>
                </Col>
              )}
            </Row>
          </Col>
          <Col span={width < breakpoint ? 24 : 8}>
            <Row>
              <Col span={4}>
                <Text strong>HA:</Text>
              </Col>
              <Col span={20}>
                <Text>{ha}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={4}>
                <Text strong>MP:</Text>
              </Col>
              <Col span={20}>
                <Text>{mp}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={4}>
                <Text strong>Dato:</Text>
              </Col>
              <Col span={20}>
                <Text>{getDate()}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={4}>
                <Text strong>Tid:</Text>
              </Col>
              <Col span={20}>
                <Text>{getTime()}</Text>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default AdminSingleEventHeader;
